import { Accordion, AccordionItem } from '@carbon/react';
import { SplitPanel } from 'components/split-panel';
import { TractDetails } from '../tract-details';
import style from './future-development.module.scss';
import { WorkflowStatusPopover } from '../workflow-status-popover';
import { useAtomValue, useSetAtom } from 'jotai';
import { Geometry } from 'geojson';
import {
  createdSubtractGeometryLoadableAtom,
  minimumDevelopmentAreaCoverageAtom,
  workflowStatusAtom,
} from '../atoms';
import { useEffect, useMemo, useState } from 'react';
import { ScopedMapPanel } from '../scoped-map-panel/scoped-map-panel';
import { SlotSummaryTable } from 'components/slot-summary-table';
import {
  useNRIReviewData,
  useTractIntersectionRequestAnalysis,
} from '../hooks';
import { CoverageThreshold } from './coverage-threshold';
import { unstable_batchedUpdates as batchedUpdates } from 'react-dom';
import { SlotFormationTable } from 'components/slots-by-formation-table';
import classNames from 'classnames';
import { SkeletonList } from './skeleton-list';
import { UnitList } from './unit-list';
import { isNil, round } from 'lodash-es';
import { FutureSlotList } from './future-slot-list';
import { UatList } from './uat-list';
import { sortUnitsByDevelopmentType } from '../utils';
import { wellStatusCounts } from 'utils/slot-categories';

export const FutureDevelopment = () => {
  const [selectedUnitId, setSelectedUnitId] = useState<string | undefined>(
    undefined
  );

  const [selectedSlotId, setSelectedSlotId] = useState<string | undefined>(
    undefined
  );

  const [selectedUatId, setSelectedUatId] = useState<number | undefined>(
    undefined
  );
  const workflowStatus = useAtomValue(workflowStatusAtom);
  const {
    isLoading,
    futureDevelopmentTractIntersectionAnalysis,
    retriggerTractIntersectionAnalysis,
  } = useTractIntersectionRequestAnalysis();
  const setMinimumDevelopmentAreaCoverage = useSetAtom(
    minimumDevelopmentAreaCoverageAtom
  );

  // NRI data from slot_allocations — used for Interest Info cards on slots/UATs.
  const { slotNRI } = useNRIReviewData();

  const slotNriByUwi = useMemo(() => {
    if (!slotNRI) return new Map();
    const map = new Map();
    for (const row of slotNRI) {
      if (row.slotId) {
        map.set(row.slotId, row);
      }
    }
    return map;
  }, [slotNRI]);

  const sortedOptimalFutureDevelopmentUnits = useMemo(() => {
    if (
      !futureDevelopmentTractIntersectionAnalysis?.optimalFutureDevelopmentUnits
    )
      return [];

    return sortUnitsByDevelopmentType(
      futureDevelopmentTractIntersectionAnalysis.optimalFutureDevelopmentUnits
    );
  }, [
    futureDevelopmentTractIntersectionAnalysis?.optimalFutureDevelopmentUnits,
  ]);

  const minimumDevelopmentAreaCoverage = useMemo(() => {
    return (
      futureDevelopmentTractIntersectionAnalysis?.parameters
        ?.minimumDevelopmentAreaCoverage ?? 0
    );
  }, [
    futureDevelopmentTractIntersectionAnalysis?.parameters
      ?.minimumDevelopmentAreaCoverage,
  ]);

  const selectedUnitName = useMemo(() => {
    return futureDevelopmentTractIntersectionAnalysis?.optimalFutureDevelopmentUnits?.find(
      (unit) => unit.id === selectedUnitId
    )?.name;
  }, [
    futureDevelopmentTractIntersectionAnalysis?.optimalFutureDevelopmentUnits,
    selectedUnitId,
  ]);

  const slotsByUnit = useMemo(() => {
    return futureDevelopmentTractIntersectionAnalysis?.slotsByUnit?.find(
      (unit) => unit.unitName === selectedUnitName
    )?.slots;
  }, [
    futureDevelopmentTractIntersectionAnalysis?.slotsByUnit,
    selectedUnitName,
  ]);

  const uatsBySlot = useMemo(() => {
    return futureDevelopmentTractIntersectionAnalysis?.unitAllocationTracts?.intersecting
      ?.find((unit) => unit.unitName === selectedUnitName)
      ?.uats?.filter((uat) => uat.wellUwis.includes(selectedSlotId ?? ''));
  }, [
    futureDevelopmentTractIntersectionAnalysis?.unitAllocationTracts,
    selectedUnitName,
    selectedSlotId,
  ]);

  // Auto-select first unit when data loads
  useEffect(() => {
    if (sortedOptimalFutureDevelopmentUnits.length > 0 && !selectedUnitId) {
      setSelectedUnitId(sortedOptimalFutureDevelopmentUnits[0].id);
    }
  }, [sortedOptimalFutureDevelopmentUnits, selectedUnitId]);

  // --- Scoped map data wiring ---

  const selectedUnitGeometry = useMemo(() => {
    if (!selectedUnitId) return null;
    const unit = sortedOptimalFutureDevelopmentUnits.find(
      (u) => u.id === selectedUnitId
    );
    return (unit?.geometry as GeoJSON.Polygon) ?? null;
  }, [sortedOptimalFutureDevelopmentUnits, selectedUnitId]);

  const subtractGeometryLoadable = useAtomValue(
    createdSubtractGeometryLoadableAtom
  );
  const subtractGeometry =
    subtractGeometryLoadable.state === 'hasData'
      ? (subtractGeometryLoadable.data?.geojson ?? null)
      : null;

  const scopedUatGeometries = useMemo(() => {
    const intersecting =
      futureDevelopmentTractIntersectionAnalysis?.unitAllocationTracts
        ?.intersecting ?? [];
    return intersecting
      .filter((unit) => unit.unitName === selectedUnitName)
      .flatMap((unit) => unit.uats)
      .filter((uat) => uat.geometry)
      .map((uat) => ({ id: uat.id, geometry: uat.geometry as Geometry }));
  }, [
    futureDevelopmentTractIntersectionAnalysis?.unitAllocationTracts
      ?.intersecting,
    selectedUnitName,
  ]);

  return (
    <SplitPanel
      left={
        <div>
          <h2 className={style.titleContainer}>Future Development</h2>
          <Accordion>
            <AccordionItem
              className={style.accordionContainer}
              title={
                <div className={style.tractDetailsTitleContainer}>
                  <span className={style.tractDetailsTitle}>
                    Tract & Subtract Details
                  </span>
                  <WorkflowStatusPopover
                    statusItems={workflowStatus.statusItems}
                    overallStatus={workflowStatus.overallStatus}
                  />
                </div>
              }
              open={true}
            >
              <div className={style.linkedItemSectionContainer}>
                <TractDetails />
              </div>
            </AccordionItem>
            <AccordionItem
              className={style.accordionContainer}
              title={
                <span className={style.tractDetailsTitle}>Slot Summary</span>
              }
              open={true}
            >
              <SlotSummaryTable
                slotSummary={
                  futureDevelopmentTractIntersectionAnalysis?.slotsSummary?.rows?.map(
                    (row) => {
                      return {
                        total: row.total,
                        wellType: row.unitType,
                        producing: row.pdp,
                        duc: row.duc,
                        permit: row.permit,
                        ltd: row.ltd,
                        shutIn: row.shutIn,
                        inactive: row.inactive,
                        ...wellStatusCounts(row),
                        expiredPermit: row.expiredPermit,
                        wellboreExcluded: row.wellboreExcluded ?? 0,
                      };
                    }
                  ) ?? []
                }
                isLoading={isLoading}
                tableId="crm-future-development-slot-summary"
              />
            </AccordionItem>
            <AccordionItem
              title={
                <span className={style.tractDetailsTitle}>
                  Future Development Configuration
                </span>
              }
              className={style.accordionContainer}
              open={true}
            >
              <div className={style.configContainer}>
                <CoverageThreshold
                  title="Find Optimal Unit Coverage Set"
                  description="Development Area Coverage Factor measures how completely a Development Area Unit covers a Subtract for development planning purposes. The FOUCS MDAC identifies the minimum coverage factor that is acceptable for an FOUCS output relative to a Subtract shape. The goal is to return an FOUCS that is as close to 100% as possible, this threshold allows for the return of a set that is less than 100% to account for minor imperfections in the GIS shapes."
                  thresholdOptions={[
                    {
                      label: '95%',
                      value: 95,
                      isSelected: minimumDevelopmentAreaCoverage * 100 === 95,
                    },
                    {
                      label: '96%',
                      value: 96,
                      isSelected: minimumDevelopmentAreaCoverage * 100 === 96,
                    },
                    {
                      label: '97%',
                      value: 97,
                      isSelected: minimumDevelopmentAreaCoverage * 100 === 97,
                    },
                    {
                      label: '98%',
                      value: 98,
                      isSelected: minimumDevelopmentAreaCoverage * 100 === 98,
                    },
                    {
                      label: '99%',
                      value: 99,
                      isSelected: minimumDevelopmentAreaCoverage * 100 === 99,
                    },
                    {
                      label: '100%',
                      value: 100,
                      isSelected: minimumDevelopmentAreaCoverage * 100 === 100,
                    },
                  ]}
                  instructions="Select a threshold to determine which Dev Areas are recognized as intersecting. Lower values include more Dev Areas with minor overlaps, while higher values filter them out. Green: Included | Red: Excluded"
                  isLoading={isLoading}
                  onThresholdChange={(threshold) => {
                    batchedUpdates(() => {
                      const normalizedThreshold = threshold / 100;
                      setMinimumDevelopmentAreaCoverage(normalizedThreshold);
                      retriggerTractIntersectionAnalysis({
                        minimumDevelopmentAreaCoverage: normalizedThreshold,
                      });
                    });
                  }}
                />
                <SlotFormationTable
                  horizontalSlotsByFormation={
                    futureDevelopmentTractIntersectionAnalysis?.horizontalSlotsByFormation
                  }
                  isLoading={isLoading}
                  tableId="crm-future-dev-slots-by-formation"
                />
                <div className={style.listContainer}>
                  <div className={style.listItemContainer}>
                    <div
                      className={classNames(style.listItem, style.unitListItem)}
                    >
                      {isLoading ? (
                        <SkeletonList title="Units" rows={3} />
                      ) : (
                        <UnitList
                          units={sortedOptimalFutureDevelopmentUnits.map(
                            (unit) => {
                              return {
                                areaAc: unit.acreage
                                  ? round(unit.acreage, 2)
                                  : undefined,
                                source: unit.source,
                                ltdCapable: unit.ltdCapable,
                                type: unit.unitDevelopmentType,
                                unitAgreementType: unit.unitAgreementType,
                                id: unit.id,
                                name: unit.name,
                                geometry: unit.geometry,
                                href: unit.href,
                                subtractToUnitPct: isNil(
                                  unit.coverage?.tractToUnit
                                )
                                  ? undefined
                                  : round(unit.coverage?.tractToUnit * 100, 2),
                                unitToSubtractPct: isNil(
                                  unit.coverage?.unitToTract
                                )
                                  ? undefined
                                  : round(unit.coverage?.unitToTract * 100, 2),
                              };
                            }
                          )}
                          selectedUnitId={selectedUnitId}
                          setSelectedUnitId={(unitId) => {
                            setSelectedUnitId(unitId);
                            if (selectedUnitId !== unitId) {
                              setSelectedSlotId(undefined);
                              setSelectedUatId(undefined);
                            }
                          }}
                        />
                      )}
                    </div>
                    <div className={style.listItem}>
                      {isLoading ? (
                        <SkeletonList title="Slots" rows={2} />
                      ) : (
                        <FutureSlotList
                          slots={slotsByUnit ?? []}
                          selectedSlotId={selectedSlotId}
                          slotNriByUwi={slotNriByUwi}
                          setSelectedSlotId={(uwi) => {
                            setSelectedSlotId(uwi);
                            if (selectedSlotId !== uwi) {
                              setSelectedUatId(undefined);
                            }
                          }}
                        />
                      )}
                    </div>
                    <div className={style.listItem}>
                      {isLoading ? (
                        <SkeletonList title="UATs" rows={1} />
                      ) : (
                        <UatList
                          uats={
                            uatsBySlot?.flat().map((uatBySlot) => {
                              return {
                                id: uatBySlot.id,
                                name: uatBySlot.tractName,
                                allocationValue: uatBySlot.allocationValue,
                                allocationFactor: round(
                                  uatBySlot.allocationFactor,
                                  2
                                ),
                                subtractToUatPct: round(
                                  uatBySlot.subtractCoverage.subtractToUat *
                                    100,
                                  2
                                ),
                                uatToSubtractPct: round(
                                  uatBySlot.subtractCoverage.uatToSubtract *
                                    100,
                                  2
                                ),
                                // Carried through so this step can say which
                                // basis produced its NRI. The choice is made on
                                // Current Development but applies per (subtract,
                                // UAT), so it moves these figures too — and
                                // dropping it here left that silent.
                                gisUuid: uatBySlot.gisUuid,
                                acreageBasis: uatBySlot.acreageBasis,
                              };
                            }) ?? []
                          }
                          slotNriByUwi={slotNriByUwi}
                          selectedSlotId={selectedSlotId}
                          selectedUatId={selectedUatId}
                          setSelectedUatId={(uatId) => {
                            batchedUpdates(() => {
                              setSelectedUatId(uatId);
                            });
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </AccordionItem>
          </Accordion>
        </div>
      }
      right={
        <ScopedMapPanel
          selectedUnitGeometry={selectedUnitGeometry}
          uatGeometries={scopedUatGeometries}
          subtractGeometry={subtractGeometry}
          selectedSlotId={selectedSlotId}
          selectedUatId={selectedUatId}
        />
      }
    />
  );
};
